import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuCheck, LuFactory, LuFlame, LuSearch, LuUsers } from 'react-icons/lu';
import { Button, Modal, ToggleSwitch } from 'components/common';
import { INPUT_CLS } from 'helpers/formStyles';

// Keys match Crm::SalesUnit::ApplyConfigurationService::GROUPS.
export const CONFIG_GROUPS = [
  { key: 'build_up_flow', label: 'Process flow · Build up', description: 'Engineering, CSD, Finance and Factory people for build-up projects', icon: LuFactory },
  { key: 'hot_roll_flow', label: 'Process flow · Hot roll', description: 'Engineering, CSD, Finance and Factory people for hot-roll projects', icon: LuFlame },
  { key: 'sales_team', label: 'Sales team', description: 'Admin, sales leader, tender and manager / chief rep', icon: LuUsers },
  { key: 'organization', label: 'Organization settings', description: 'Region, site, type of sales and sales office', icon: LuBuilding2 },
];

const DEFAULT_GROUPS = ['build_up_flow', 'hot_roll_flow'];

const matches = (unit, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [unit.name, unit.description, unit.region].some((value) => String(value || '').toLowerCase().includes(q));
};

const toggleIn = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

const GroupList = ({ groups, onToggle, disabled }) => (
  <div className="tw-divide-y tw-divide-slate-100 tw-border tw-border-solid tw-border-slate-200 tw-rounded">
    {CONFIG_GROUPS.map(({ key, label, description, icon: Icon }) => (
      <div key={key} className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5">
        <Icon size={16} className="tw-shrink-0 tw-text-slate-500" />
        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-text-sm tw-font-semibold tw-text-slate-800">{label}</div>
          <div className="tw-text-xs tw-text-slate-500">{description}</div>
        </div>
        <ToggleSwitch size="md" value={groups.includes(key)} disabled={disabled} onChange={() => onToggle(key)} />
      </div>
    ))}
  </div>
);

GroupList.propTypes = { groups: PropTypes.array.isRequired, onToggle: PropTypes.func.isRequired, disabled: PropTypes.bool };

const TargetList = ({ units, selectedIds, onChange, disabled }) => {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => units.filter((unit) => matches(unit, query)), [units, query]);
  const visibleIds = visible.map((unit) => unit.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleVisible = () => onChange(allVisibleSelected
    ? selectedIds.filter((id) => !visibleIds.includes(id))
    : [...new Set([...selectedIds, ...visibleIds])]);

  return (
    <div className="tw-border tw-border-solid tw-border-slate-200 tw-rounded">
      <div className="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100">
        <div className="tw-relative tw-flex-1">
          <LuSearch size={14} className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400" />
          <input className={`${INPUT_CLS} tw-pl-8`} placeholder="Search name, description, region…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <Button size="sm" variant="secondary" disabled={disabled || visible.length === 0} onClick={toggleVisible}>
          {allVisibleSelected ? 'Clear' : 'Select all'}{query ? ' (filtered)' : ''}
        </Button>
      </div>
      <ul className="tw-list-none tw-m-0 tw-p-0 tw-max-h-64 tw-overflow-y-auto">
        {visible.length === 0 && <li className="tw-px-3 tw-py-6 tw-text-center tw-text-xs tw-text-slate-400">No sales units match.</li>}
        {visible.map((unit) => (
          <li key={unit.id}>
            <label className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-mb-0 tw-cursor-pointer hover:tw-bg-slate-50">
              <input type="checkbox" disabled={disabled} checked={selectedIds.includes(unit.id)} onChange={() => onChange(toggleIn(selectedIds, unit.id))} />
              <span className="tw-w-20 tw-shrink-0 tw-text-sm tw-font-semibold tw-text-slate-800">{unit.name}</span>
              <span className="tw-flex-1 tw-truncate tw-text-xs tw-text-slate-500">{unit.description || '—'}</span>
              <span className="tw-shrink-0 tw-text-[11px] tw-text-slate-400">{unit.region}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};

TargetList.propTypes = {
  units: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

/**
 * Copy configuration groups from `salesUnit` onto other units. `onApply(targetIds, groups)`
 * does the request, so the same dialog serves "any / all sales units" and "children only".
 */
const ApplyConfigurationModal = ({ show, onHide, salesUnit, candidates, initialSelectedIds, onApply }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [groups, setGroups] = useState(DEFAULT_GROUPS);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSelectedIds(initialSelectedIds || []);
    setGroups(DEFAULT_GROUPS);
  }, [show, initialSelectedIds]);

  const units = useMemo(() => candidates.filter((unit) => unit.id !== salesUnit?.id), [candidates, salesUnit?.id]);

  const apply = async () => {
    setApplying(true);
    try {
      await onApply(selectedIds, groups);
      onHide();
    } catch (error) {
      // onApply already reported the failure; keep the dialog open to retry.
    } finally {
      setApplying(false);
    }
  };

  const footer = (
    <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3">
      <span className="tw-text-xs tw-text-slate-500">
        {selectedIds.length} of {units.length} sales unit{units.length === 1 ? '' : 's'} selected · {groups.length} group{groups.length === 1 ? '' : 's'}
      </span>
      <div className="tw-flex tw-gap-2">
        <Button variant="secondary" disabled={applying} onClick={onHide}>Cancel</Button>
        <Button variant="primary" leftIcon={LuCheck} loading={applying} disabled={selectedIds.length === 0 || groups.length === 0} onClick={apply}>
          Apply to {selectedIds.length} sales unit{selectedIds.length === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" title={`Apply configuration from ${salesUnit?.name || ''}`} footer={footer}>
      <h6 className="tw-text-[11px] tw-font-bold tw-text-primary-600 tw-uppercase tw-tracking-wider tw-mb-2">1 · What to copy</h6>
      <GroupList groups={groups} disabled={applying} onToggle={(key) => setGroups((current) => toggleIn(current, key))} />

      <h6 className="tw-text-[11px] tw-font-bold tw-text-primary-600 tw-uppercase tw-tracking-wider tw-mt-5 tw-mb-2">2 · Apply to</h6>
      <TargetList units={units} selectedIds={selectedIds} onChange={setSelectedIds} disabled={applying} />

      <p className="tw-mb-0 tw-mt-3 tw-text-xs tw-text-amber-700">
        Values in the selected groups will be replaced on every selected sales unit.
      </p>
    </Modal>
  );
};

ApplyConfigurationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  salesUnit: PropTypes.object,
  candidates: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string })).isRequired,
  initialSelectedIds: PropTypes.arrayOf(PropTypes.number),
  onApply: PropTypes.func.isRequired,
};

export default ApplyConfigurationModal;
